/**
 * TankSelectRenderer — SF2/Melee character select grid with dual info panels.
 *
 * Extracted from MenuRenderer as a composition class.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { Keys } from 'bonkjs';
import { WORLD, MENU, PLAYER_COLORS, TANK_SELECT, TANK_VISUAL } from '../../constants';
import { getBackButtonLabel } from '../input-hints';
import { lookupTank, getAllTankIds, getEffectiveShots, getVisibleTankIds, getShowBonusTanks, getGridDimensions, isTankDisabled } from '../../tanks';
import { drawTankShape, lighten } from '../shapes';
import { MenuEyePreview } from '../eyes';
import type { GameState } from '../../types';
import { isTankUnlocked, getTankPrice, purchaseTank, getCoins } from '../../progression/storage';

function monoStyle(size: number, color: number, bold = false): TextStyle {
    return new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: size,
        fill: color,
        fontWeight: bold ? 'bold' : 'normal',
    });
}

interface CellRefs {
    hexGfx: Graphics;
    turretGfx: Graphics;
    nameText: Text;
    lockText: Text | null;
    hitRect: Graphics;
}

export class TankSelectRenderer {
    private tankSelectTitle: Text;
    private tankSelectHint: Text;
    private bonusHint: Text;
    private tankSelectCells: Container[] = [];
    private tankSelectCellBgs: Graphics[] = [];
    private tankSelectCellRefs: CellRefs[] = [];
    private lastCoins = -1;
    private tankSelectCursorGfx: Graphics;
    private tankSelectInfoPanels: Container[] = [];
    private tankSelectInfoLabels: Text[] = [];
    private tankSelectInfoNames: Text[] = [];
    private tankSelectInfoHexes: Graphics[] = [];
    private tankSelectInfoDescs: Text[] = [];
    private tankSelectInfoTiers: Text[][] = [[], []];
    private tankSelectInfoReady: Text[] = [];
    private tankSelectBackButton: Text;
    private tankSelectBackHitRect: Graphics;
    private tankSelectGridEyes: MenuEyePreview;
    private tankSelectInfoEyes: MenuEyePreview;
    private lastVisibleIds: string[] = [];

    constructor(
        container: Container,
        tapVirtualKey: (code: string) => void,
        getState: () => GameState | null,
    ) {
        const cx = WORLD.CANVAS_WIDTH / 2;

        // Full-screen event blocker
        const tankSelectHitArea = new Graphics();
        tankSelectHitArea.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        tankSelectHitArea.fill({ color: 0x000000, alpha: 0.001 });
        tankSelectHitArea.eventMode = 'static';
        container.addChild(tankSelectHitArea);

        // Title
        this.tankSelectTitle = new Text({
            text: 'SELECT TANK',
            style: monoStyle(MENU.MODE_SELECT_TITLE_SIZE, 0xffffff, true),
        });
        this.tankSelectTitle.anchor.set(0.5);
        this.tankSelectTitle.position.set(cx, 60);
        container.addChild(this.tankSelectTitle);

        // Eye previews
        this.tankSelectGridEyes = new MenuEyePreview();
        this.tankSelectInfoEyes = new MenuEyePreview();

        // Build max grid (6x2 = 12 cells) — content populated by rebuildCellContent
        const { COLS, ROWS, CELL_SIZE, CELL_GAP, GRID_TOP_Y, CELL_HEX_RADIUS, CELL_NAME_SIZE, CELL_NAME_COLOR, CELL_BG_COLOR, CELL_BG_ALPHA, CELL_CORNER_RADIUS } = TANK_SELECT;

        for (let i = 0; i < COLS * ROWS; i++) {
            const cell = new Container();
            cell.visible = false; // will be positioned/shown by rebuildCellContent
            container.addChild(cell);

            // Background rect
            const bg = new Graphics();
            bg.roundRect(0, 0, CELL_SIZE, CELL_SIZE, CELL_CORNER_RADIUS);
            bg.fill({ color: CELL_BG_COLOR, alpha: CELL_BG_ALPHA });
            cell.addChild(bg);
            this.tankSelectCellBgs.push(bg);

            // Mini tank wireframe shape (placeholder — redrawn in rebuildCellContent)
            const hexGfx = new Graphics();
            hexGfx.position.set(CELL_SIZE / 2, CELL_SIZE / 2 - 12);
            cell.addChild(hexGfx);

            // Mini wireframe turret (placeholder — redrawn in rebuildCellContent)
            const turretGfx = new Graphics();
            turretGfx.position.set(CELL_SIZE / 2, CELL_SIZE / 2 - 12);
            cell.addChild(turretGfx);

            // Tank name
            const nameText = new Text({
                text: '',
                style: monoStyle(CELL_NAME_SIZE, CELL_NAME_COLOR, true),
            });
            nameText.anchor.set(0.5);
            nameText.position.set(CELL_SIZE / 2, CELL_SIZE - 18);
            cell.addChild(nameText);

            // "COMING SOON" overlay (hidden by default)
            const lockText = new Text({
                text: 'COMING\nSOON',
                style: monoStyle(14, 0x666666, true),
            });
            lockText.anchor.set(0.5);
            lockText.position.set(CELL_SIZE / 2, CELL_SIZE / 2 - 4);
            lockText.visible = false;
            cell.addChild(lockText);

            // Click/hover hit rect
            const hitRect = new Graphics();
            hitRect.rect(0, 0, CELL_SIZE, CELL_SIZE);
            hitRect.fill({ color: 0x000000, alpha: 0.001 });
            hitRect.eventMode = 'static';
            hitRect.cursor = 'pointer';
            const cellIdx = i;
            hitRect.on('pointerdown', () => {
                const state = getState();
                if (!state) return;
                const isOnline = !!state.multiplayer?.enabled;
                const currentTankIds = getVisibleTankIds(isOnline);
                if (cellIdx >= currentTankIds.length) return;
                const tankId = currentTankIds[cellIdx];
                if (isTankDisabled(tankId)) return;
                // Attempt purchase if locked
                if (!isTankUnlocked(tankId)) {
                    if (purchaseTank(tankId)) {
                        this.rebuildCellContent(currentTankIds);
                    }
                    return;
                }
                if (isOnline) {
                    const mySlot = state.multiplayer!.mySlot;
                    if (!state.tankSelectReady[mySlot]) {
                        state.tankSelectCursor[mySlot] = cellIdx;
                        state.players[mySlot].tankId = tankId;
                        state.players[mySlot].color = lookupTank(tankId).visual.bodyColor;
                    }
                } else {
                    for (let p = 0; p < state.players.length && p < 2; p++) {
                        if (!state.tankSelectReady[p]) {
                            state.tankSelectCursor[p] = cellIdx;
                            state.players[p].tankId = tankId;
                            state.players[p].color = lookupTank(tankId).visual.bodyColor;
                            state.tankSelectReady[p] = true;
                            break;
                        }
                    }
                }
                tapVirtualKey(Keys.Space);
            });
            hitRect.on('pointerover', () => {
                const state = getState();
                if (!state) return;
                const isOnline = !!state.multiplayer?.enabled;
                const currentTankIds = getVisibleTankIds(isOnline);
                if (cellIdx >= currentTankIds.length) return;
                const tankId = currentTankIds[cellIdx];
                if (isTankDisabled(tankId)) return;
                if (isOnline) {
                    const mySlot = state.multiplayer!.mySlot;
                    if (!state.tankSelectReady[mySlot]) {
                        state.tankSelectCursor[mySlot] = cellIdx;
                        state.players[mySlot].tankId = tankId;
                        state.players[mySlot].color = lookupTank(tankId).visual.bodyColor;
                    }
                } else {
                    for (let p = 0; p < state.players.length && p < 2; p++) {
                        if (!state.tankSelectReady[p]) {
                            state.tankSelectCursor[p] = cellIdx;
                            state.players[p].tankId = tankId;
                            state.players[p].color = lookupTank(tankId).visual.bodyColor;
                            break;
                        }
                    }
                }
            });
            cell.addChild(hitRect);
            this.tankSelectCells.push(cell);
            this.tankSelectCellRefs.push({ hexGfx, turretGfx, nameText, lockText, hitRect });
        }

        // Cursor graphics
        this.tankSelectCursorGfx = new Graphics();
        container.addChild(this.tankSelectCursorGfx);

        // Info panels (P1 left, P2 right) — positioned dynamically in update()
        const { INFO_PANEL_WIDTH, INFO_PANEL_TOP_Y, INFO_NAME_SIZE, INFO_DESC_SIZE, INFO_DESC_COLOR, INFO_HEX_RADIUS, INFO_TIER_SIZE, INFO_READY_SIZE, INFO_READY_COLOR, INFO_LABEL_SIZE } = TANK_SELECT;

        for (let p = 0; p < 2; p++) {
            const panel = new Container();
            panel.position.set(0, INFO_PANEL_TOP_Y); // x set in update()
            container.addChild(panel);
            this.tankSelectInfoPanels.push(panel);

            // Player label
            const label = new Text({ text: `P${p + 1}`, style: monoStyle(INFO_LABEL_SIZE, PLAYER_COLORS[p], true) });
            label.anchor.set(0.5);
            label.position.set(INFO_PANEL_WIDTH / 2, 0);
            panel.addChild(label);
            this.tankSelectInfoLabels.push(label);

            // Hex preview
            const hexPreview = new Graphics();
            hexPreview.position.set(INFO_PANEL_WIDTH / 2, 60);
            panel.addChild(hexPreview);
            this.tankSelectInfoHexes.push(hexPreview);

            // Eyes on info panel hex
            const infoEyeContainer = this.tankSelectInfoEyes.addSlot('mortar');
            const infoEyeScale = INFO_HEX_RADIUS / 25 * 0.8;
            infoEyeContainer.scale.set(infoEyeScale);
            infoEyeContainer.position.set(INFO_PANEL_WIDTH / 2, 60 + INFO_HEX_RADIUS * -0.15);
            panel.addChild(infoEyeContainer);

            // Tank name
            const nameText = new Text({ text: '', style: monoStyle(INFO_NAME_SIZE, 0xffffff, true) });
            nameText.anchor.set(0.5);
            nameText.position.set(INFO_PANEL_WIDTH / 2, 115);
            panel.addChild(nameText);
            this.tankSelectInfoNames.push(nameText);

            // Description
            const descText = new Text({ text: '', style: new TextStyle({
                fontFamily: '"JetBrains Mono", monospace', fontSize: INFO_DESC_SIZE, fill: INFO_DESC_COLOR,
                wordWrap: true, wordWrapWidth: INFO_PANEL_WIDTH - 10,
            })});
            descText.anchor.set(0.5, 0);
            descText.position.set(INFO_PANEL_WIDTH / 2, 145);
            panel.addChild(descText);
            this.tankSelectInfoDescs.push(descText);

            // 3 tier labels
            for (let t = 0; t < 3; t++) {
                const tierText = new Text({ text: '', style: monoStyle(INFO_TIER_SIZE, 0xaaaaaa) });
                tierText.anchor.set(0, 0.5);
                tierText.position.set(10, 220 + t * 22);
                panel.addChild(tierText);
                this.tankSelectInfoTiers[p].push(tierText);
            }

            // READY indicator
            const readyText = new Text({ text: '', style: monoStyle(INFO_READY_SIZE, INFO_READY_COLOR, true) });
            readyText.anchor.set(0.5);
            readyText.position.set(INFO_PANEL_WIDTH / 2, 310);
            panel.addChild(readyText);
            this.tankSelectInfoReady.push(readyText);
        }

        // Back button
        this.tankSelectBackHitRect = new Graphics();
        this.tankSelectBackHitRect.eventMode = 'static';
        this.tankSelectBackHitRect.cursor = 'pointer';
        this.tankSelectBackHitRect.on('pointerdown', () => tapVirtualKey(Keys.Escape));
        container.addChild(this.tankSelectBackHitRect);

        this.tankSelectBackButton = new Text({
            text: getBackButtonLabel(),
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.tankSelectBackButton.anchor.set(0.5);
        this.tankSelectBackButton.position.set(cx - 300, WORLD.CANVAS_HEIGHT - 40);
        container.addChild(this.tankSelectBackButton);

        // Hint
        this.tankSelectHint = new Text({
            text: 'P1: A/D/W/S + SPACE  |  P2: Arrows + ENTER  |  ESC = back',
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.tankSelectHint.anchor.set(0.5);
        this.tankSelectHint.position.set(cx, WORLD.CANVAS_HEIGHT - 40);
        container.addChild(this.tankSelectHint);

        // Bonus toggle hint (online only)
        this.bonusHint = new Text({
            text: '= BONUS TANKS',
            style: monoStyle(MENU.HINT_FONT_SIZE, MENU.HINT_COLOR),
        });
        this.bonusHint.anchor.set(0.5);
        this.bonusHint.position.set(cx + 300, WORLD.CANVAS_HEIGHT - 40);
        this.bonusHint.visible = false;
        container.addChild(this.bonusHint);

        // Initial cell content build
        this.rebuildCellContent(getAllTankIds());
    }

    /** Rebuild cell visual content for the current visible tank list. */
    private rebuildCellContent(visibleIds: string[]): void {
        const { CELL_SIZE, CELL_GAP, GRID_TOP_Y, CELL_HEX_RADIUS, CELL_NAME_SIZE, CELL_NAME_COLOR } = TANK_SELECT;
        const { cols } = getGridDimensions(visibleIds.length);
        const gridW = cols * CELL_SIZE + (cols - 1) * CELL_GAP;
        const cx = WORLD.CANVAS_WIDTH / 2;
        const gridLeft = cx - gridW / 2;

        for (let i = 0; i < this.tankSelectCells.length; i++) {
            const cell = this.tankSelectCells[i];
            const refs = this.tankSelectCellRefs[i];

            if (i >= visibleIds.length) {
                cell.visible = false;
                continue;
            }

            cell.visible = true;
            const col = i % cols;
            const row = Math.floor(i / cols);
            cell.position.set(
                gridLeft + col * (CELL_SIZE + CELL_GAP),
                GRID_TOP_Y + row * (CELL_SIZE + CELL_GAP),
            );

            const tankDef = lookupTank(visibleIds[i]);
            const disabled = tankDef.disabled === true;
            const locked = !isTankUnlocked(visibleIds[i]);
            const dimmed = disabled || locked;
            const tankColor = tankDef.visual.bodyColor;

            // Redraw hex shape
            refs.hexGfx.clear();
            drawTankShape(refs.hexGfx, visibleIds[i], CELL_HEX_RADIUS, tankColor);
            refs.hexGfx.alpha = dimmed ? 0.25 : 1;

            // Redraw turret
            const coreColor = lighten(tankColor, 0.65);
            refs.turretGfx.clear();
            refs.turretGfx.moveTo(0, 0);
            refs.turretGfx.lineTo(CELL_HEX_RADIUS + 6, 0);
            refs.turretGfx.stroke({ color: coreColor, width: 1.2 });
            refs.turretGfx.circle(CELL_HEX_RADIUS + 6, 0, 3);
            refs.turretGfx.stroke({ color: coreColor, width: 1.2 });
            refs.turretGfx.alpha = dimmed ? 0.25 : 1;

            // Update name
            refs.nameText.text = tankDef.name.toUpperCase();
            (refs.nameText.style as TextStyle).fill = dimmed ? 0x444444 : CELL_NAME_COLOR;

            // Lock overlay — show price for locked tanks, "COMING SOON" for disabled
            if (disabled) {
                refs.lockText!.text = 'COMING\nSOON';
                (refs.lockText!.style as TextStyle).fill = 0x666666;
                refs.lockText!.visible = true;
            } else if (locked) {
                const price = getTankPrice(visibleIds[i]);
                refs.lockText!.text = `${price}`;
                (refs.lockText!.style as TextStyle).fill = 0xffd700;
                refs.lockText!.visible = true;
            } else {
                refs.lockText!.visible = false;
            }

            // Hit rect cursor
            refs.hitRect.cursor = disabled ? 'default' : 'pointer';
        }

        this.lastVisibleIds = visibleIds.slice();
    }

    update(state: GameState, elapsed: number, dt: number): void {
        const isOnline = !!state.multiplayer?.enabled;
        const tankIds = getVisibleTankIds(isOnline);
        const tankCount = tankIds.length;
        const { cols } = getGridDimensions(tankCount);
        const { CELL_SIZE, CELL_GAP, CELL_CORNER_RADIUS, CELL_LOCKED_ALPHA, CURSOR_WIDTH, CURSOR_BRACKET_SIZE, CURSOR_PULSE_SPEED, CURSOR_PULSE_MIN, CURSOR_LOCKED_ALPHA, INFO_HEX_RADIUS, INFO_PANEL_WIDTH, INFO_PANEL_MARGIN } = TANK_SELECT;
        const gridW = cols * CELL_SIZE + (cols - 1) * CELL_GAP;
        const cx = WORLD.CANVAS_WIDTH / 2;
        const gridLeft = cx - gridW / 2;

        // Rebuild cell content if visible tank list changed or coins changed (purchase)
        const currentCoins = getCoins();
        if (tankIds.length !== this.lastVisibleIds.length || tankIds.some((id, i) => id !== this.lastVisibleIds[i]) || currentCoins !== this.lastCoins) {
            this.rebuildCellContent(tankIds);
            this.lastCoins = currentCoins;
        }

        // Redraw cell backgrounds (locked cells get player-color fill)
        for (let i = 0; i < this.tankSelectCellBgs.length; i++) {
            if (!this.tankSelectCells[i].visible) continue;
            const bg = this.tankSelectCellBgs[i];
            bg.clear();
            bg.roundRect(0, 0, CELL_SIZE, CELL_SIZE, CELL_CORNER_RADIUS);
            bg.fill({ color: TANK_SELECT.CELL_BG_COLOR, alpha: TANK_SELECT.CELL_BG_ALPHA });

            for (let p = 0; p < 2; p++) {
                if (state.tankSelectReady[p] && state.tankSelectCursor[p] === i) {
                    bg.roundRect(0, 0, CELL_SIZE, CELL_SIZE, CELL_CORNER_RADIUS);
                    bg.fill({ color: PLAYER_COLORS[p], alpha: CELL_LOCKED_ALPHA });
                }
            }
        }

        // Redraw cursors
        this.tankSelectCursorGfx.clear();
        const playerCount = state.players.length;
        for (let p = 0; p < Math.min(2, playerCount); p++) {
            const ci = state.tankSelectCursor[p];
            if (ci >= tankCount) continue; // opponent on a bonus tank we're not showing
            const col = ci % cols;
            const row = Math.floor(ci / cols);
            const cellX = gridLeft + col * (CELL_SIZE + CELL_GAP);
            const cellY = TANK_SELECT.GRID_TOP_Y + row * (CELL_SIZE + CELL_GAP);
            const color = PLAYER_COLORS[p];

            const locked = state.tankSelectReady[p];
            const pulse = locked ? CURSOR_LOCKED_ALPHA : CURSOR_PULSE_MIN + (1 - CURSOR_PULSE_MIN) * (0.5 + 0.5 * Math.sin(elapsed * CURSOR_PULSE_SPEED + p * Math.PI));
            const pad = 4 + p * 3;
            const x0 = cellX - pad;
            const y0 = cellY - pad;
            const x1 = cellX + CELL_SIZE + pad;
            const y1 = cellY + CELL_SIZE + pad;
            const bk = CURSOR_BRACKET_SIZE;

            this.tankSelectCursorGfx.moveTo(x0, y0 + bk).lineTo(x0, y0).lineTo(x0 + bk, y0);
            this.tankSelectCursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
            this.tankSelectCursorGfx.moveTo(x1 - bk, y0).lineTo(x1, y0).lineTo(x1, y0 + bk);
            this.tankSelectCursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
            this.tankSelectCursorGfx.moveTo(x1, y1 - bk).lineTo(x1, y1).lineTo(x1 - bk, y1);
            this.tankSelectCursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
            this.tankSelectCursorGfx.moveTo(x0 + bk, y1).lineTo(x0, y1).lineTo(x0, y1 - bk);
            this.tankSelectCursorGfx.stroke({ color, width: CURSOR_WIDTH, alpha: pulse });
        }

        // Reposition info panels based on current grid width
        this.tankSelectInfoPanels[0].position.x = gridLeft - INFO_PANEL_WIDTH - INFO_PANEL_MARGIN;
        this.tankSelectInfoPanels[1].position.x = gridLeft + gridW + INFO_PANEL_MARGIN;

        // Update info panels
        for (let p = 0; p < 2; p++) {
            if (!state.players[p]) {
                this.tankSelectInfoPanels[p].visible = false;
                continue;
            }
            this.tankSelectInfoPanels[p].visible = true;
            const tank = lookupTank(state.players[p].tankId);

            if (state.multiplayer?.enabled) {
                const mySlot = state.multiplayer.mySlot;
                this.tankSelectInfoLabels[p].text = p === mySlot ? 'YOU' : (state.multiplayer.opponentName || `P${p + 1}`);
            } else {
                const pData = state.players[p];
                const aiTag = pData.isAI ? ` Lv.${pData.aiDifficulty}` : '';
                this.tankSelectInfoLabels[p].text = `${pData.name}${aiTag}`;
            }

            const hexGfx = this.tankSelectInfoHexes[p];
            hexGfx.clear();
            const tankColor = tank.visual.bodyColor;
            drawTankShape(hexGfx, state.players[p].tankId, INFO_HEX_RADIUS, tankColor);
            const infoCore = lighten(tankColor, 0.65);
            hexGfx.moveTo(0, 0);
            hexGfx.lineTo(INFO_HEX_RADIUS + 10, 0);
            hexGfx.stroke({ color: infoCore, width: 1.2 });
            hexGfx.circle(INFO_HEX_RADIUS + 10, 0, 3);
            hexGfx.stroke({ color: infoCore, width: 1.2 });

            const isTerraformer = state.players[p].tankId === 'terraformer';
            const shots = getEffectiveShots(state.players[p].tankId, state.players[p].terraformerMode);
            if (isTerraformer) {
                const modeTag = state.players[p].terraformerMode === 'sapper' ? ' [SAP]' : ' [MAS]';
                this.tankSelectInfoNames[p].text = tank.name.toUpperCase() + modeTag;
            } else {
                this.tankSelectInfoNames[p].text = tank.name.toUpperCase();
            }
            (this.tankSelectInfoNames[p].style as TextStyle).fill = shots[0].tierColor;

            if (isTerraformer && !state.tankSelectReady[p]) {
                const toggleKey = p === 0 ? 'TAB' : 'SHIFT';
                this.tankSelectInfoDescs[p].text = tank.description + `\n${toggleKey} = toggle mode`;
            } else {
                this.tankSelectInfoDescs[p].text = tank.description;
            }

            for (let t = 0; t < 3; t++) {
                if (t >= shots.length) {
                    this.tankSelectInfoTiers[p][t].text = '';
                    continue;
                }
                const shot = shots[t];
                this.tankSelectInfoTiers[p][t].text = `${t + 1}: ${shot.label}`;
                (this.tankSelectInfoTiers[p][t].style as TextStyle).fill = shot.tierColor;
            }

            this.tankSelectInfoReady[p].text = state.tankSelectReady[p] ? 'READY' : '';
        }

        // Hint text
        if (state.twoGamepadsConnected) {
            this.tankSelectHint.text = 'P1: Left Stick + A  |  P2: Left Stick + A  |  B = back';
            this.tankSelectHint.visible = true;
        } else if (!isOnline) {
            this.tankSelectHint.text = 'Q = toggle CPU  |  E = CPU level';
            this.tankSelectHint.visible = true;
        } else {
            this.tankSelectHint.text = 'WASD + SPACE to select  |  ESC = back';
            this.tankSelectHint.visible = true;
        }

        // Bonus toggle hint (online only, before confirming)
        if (isOnline && !state.tankSelectReady[state.multiplayer!.mySlot]) {
            this.bonusHint.visible = true;
            this.bonusHint.text = getShowBonusTanks() ? '= HIDE BONUS' : '= BONUS TANKS';
        } else {
            this.bonusHint.visible = false;
        }

        // Back button
        this.tankSelectBackButton.visible = true;
        this.tankSelectBackHitRect.visible = true;
        const backW = Math.max(200, this.tankSelectBackButton.width + 40);
        const backH = 40;
        this.tankSelectBackHitRect.clear();
        this.tankSelectBackHitRect.rect(0, 0, backW, backH);
        this.tankSelectBackHitRect.fill({ color: 0x000000, alpha: 0.001 });
        this.tankSelectBackHitRect.position.set(cx - 300 - backW / 2, WORLD.CANVAS_HEIGHT - 40 - backH / 2);

        // Eye previews
        this.tankSelectGridEyes.update(dt);
        this.tankSelectInfoEyes.update(dt);
    }

    destroy(): void {
        this.tankSelectGridEyes.destroy();
        this.tankSelectInfoEyes.destroy();
        // Rest destroyed with container by parent
    }
}
